/**
 * StorageStep Component
 * Extracted from DeviceSpecifications.tsx
 * Handles storage capacity selection UI and logic
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HardDrive } from 'lucide-react';
import styled, { keyframes } from 'styled-components';

// Animations for device illustration (matching QuestionStep)
const phoneSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-80px);
  }
  to {
    opacity: 1;
    transform: translateX(-40px);
  }
`;

const phoneSlideInWide = keyframes`
  from {
    opacity: 0;
    transform: translateX(-100px);
  }
  to {
    opacity: 1;
    transform: translateX(-64px);
  }
`;

// Storage helper functions
const getIphoneStorageValues = (modelNameRaw: string): string[] => {
  const name = (modelNameRaw || '').toLowerCase();

  // iPhone 18 series (2026): Pro and Pro Max start at 256GB and go up to 2TB
  if (name.includes('iphone 18 pro max') || name.includes('iphone 18 pro')) {
    return ['256GB', '512GB', '1TB', '2TB'];
  }

  // iPhone 17 series
  if (name.includes('iphone 17 pro max') || name.includes('iphone 17 pro')) {
    return ['128GB', '256GB', '512GB', '1TB'];
  }
  if (name.includes('iphone 17 air') || name === 'iphone 17') {
    return ['128GB', '256GB', '512GB', '1TB'];
  }

  // iPhone 16 series
  if (name.includes('iphone 16 pro max') || name.includes('iphone 16 pro')) {
    return ['128GB', '256GB', '512GB', '1TB'];
  }
  if (name.includes('iphone 16 plus') || name === 'iphone 16' || name.includes('iphone 16e')) {
    return ['128GB', '256GB', '512GB'];
  }

  // iPhone 15 series
  if (name.includes('iphone 15 pro max') || name.includes('iphone 15 pro')) {
    return ['128GB', '256GB', '512GB', '1TB'];
  }
  if (name.includes('iphone 15 plus') || name === 'iphone 15') {
    return ['128GB', '256GB', '512GB'];
  }

  // iPhone 14 series
  if (name.includes('iphone 14 pro max') || name.includes('iphone 14 pro')) {
    return ['128GB', '256GB', '512GB', '1TB'];
  }
  if (name.includes('iphone 14 plus') || name === 'iphone 14') {
    return ['128GB', '256GB', '512GB'];
  }

  // iPhone 13 series
  if (name.includes('iphone 13 pro max') || name.includes('iphone 13 pro')) {
    return ['128GB', '256GB', '512GB', '1TB'];
  }
  if (name.includes('iphone 13') || name.includes('iphone 13 mini')) {
    return ['128GB', '256GB', '512GB'];
  }

  // iPhone 12 series
  if (name.includes('iphone 12 pro max') || name.includes('iphone 12 pro')) {
    return ['128GB', '256GB', '512GB'];
  }
  if (name.includes('iphone 12') || name.includes('iphone 12 mini')) {
    return ['64GB', '128GB', '256GB'];
  }

  // iPhone 11 series
  if (name.includes('iphone 11 pro max') || name.includes('iphone 11 pro')) {
    return ['64GB', '256GB', '512GB'];
  }
  if (name === 'iphone 11') {
    return ['64GB', '128GB', '256GB'];
  }

  // iPhone X series
  if (name.includes('iphone xs max') || name.includes('iphone xs')) {
    return ['64GB', '256GB', '512GB'];
  }
  if (name.includes('iphone xr')) {
    return ['64GB', '128GB', '256GB'];
  }
  if (name === 'iphone x') {
    return ['64GB', '256GB'];
  }

  // iPhone 8 series
  if (name.includes('iphone 8 plus') || name === 'iphone 8') {
    return ['64GB', '128GB', '256GB'];
  }

  // iPhone 7 series
  if (name.includes('iphone 7 plus') || name === 'iphone 7') {
    return ['32GB', '128GB', '256GB'];
  }

  // iPhone SE
  if (name.includes('iphone se')) {
    return ['64GB', '128GB', '256GB'];
  }

  // Fallback
  return ['128GB', '256GB', '512GB'];
};

const getSamsungStorageValues = (modelNameRaw: string): string[] => {
  const name = (modelNameRaw || '').toLowerCase();
  const key = name.replace(/\s+|\-/g, '');

  // Try robust regex parsing for S-series
  const sMatch = name.match(/s\s*([0-9]{1,2})\s*(ultra|plus|fe)?/i);
  if (sMatch) {
    const series = parseInt(sMatch[1], 10);
    const variant = (sMatch[2] || '').toLowerCase();
    const bySeries: Record<number, Record<string, string[]>> = {
      25: {
        ultra: ['256GB', '512GB', '1TB'],
        plus: ['256GB', '512GB'],
        base: ['128GB', '256GB', '512GB']
      },
      24: {
        ultra: ['256GB', '512GB', '1TB'],
        plus: ['256GB', '512GB'],
        base: ['128GB', '256GB', '512GB']
      },
      23: {
        ultra: ['256GB', '512GB', '1TB'],
        plus: ['256GB', '512GB'],
        base: ['128GB', '256GB', '512GB']
      },
      22: {
        ultra: ['128GB', '256GB', '512GB', '1TB'],
        plus: ['128GB', '256GB'],
        base: ['128GB', '256GB']
      },
      21: {
        ultra: ['128GB', '256GB', '512GB'],
        plus: ['128GB', '256GB'],
        fe: ['128GB', '256GB'],
        base: ['128GB', '256GB']
      },
      20: {
        ultra: ['128GB', '256GB', '512GB'],
        plus: ['128GB', '256GB', '512GB'],
        fe: ['128GB', '256GB'],
        base: ['128GB']
      },
      10: {
        plus: ['128GB', '512GB', '1TB'],
        e: ['128GB', '256GB'],
        base: ['128GB', '512GB']
      },
      9: {
        plus: ['64GB', '128GB', '256GB'],
        base: ['64GB', '128GB', '256GB']
      },
      8: {
        base: ['64GB']
      }
    };
    const seriesMap = bySeries[series as keyof typeof bySeries];
    if (seriesMap) {
      if (variant && seriesMap[variant]) return seriesMap[variant];
      return seriesMap.base || ['128GB', '256GB'];
    }
  }

  // Galaxy S25
  if (key.includes('s25ultra')) return ['256GB', '512GB', '1TB'];
  if (key.includes('s25plus')) return ['256GB', '512GB'];
  if (key.includes('s25')) return ['128GB', '256GB', '512GB'];

  // Galaxy S24
  if (key.includes('s24ultra')) return ['256GB', '512GB', '1TB'];
  if (key.includes('s24plus')) return ['256GB', '512GB'];
  if (key.includes('s24')) return ['128GB', '256GB', '512GB'];

  // Galaxy S23
  if (key.includes('s23ultra')) return ['256GB', '512GB', '1TB'];
  if (key.includes('s23plus')) return ['256GB', '512GB'];
  if (key.includes('s23')) return ['128GB', '256GB', '512GB'];

  // Galaxy S22
  if (key.includes('s22ultra')) return ['128GB', '256GB', '512GB', '1TB'];
  if (key.includes('s22plus')) return ['128GB', '256GB'];
  if (key.includes('s22')) return ['128GB', '256GB'];

  // Galaxy S21
  if (key.includes('s21ultra')) return ['128GB', '256GB', '512GB'];
  if (key.includes('s21plus')) return ['128GB', '256GB'];
  if (key.includes('s21fe')) return ['128GB', '256GB'];
  if (key.includes('s21')) return ['128GB', '256GB'];

  // Galaxy S20
  if (key.includes('s20ultra')) return ['128GB', '256GB', '512GB'];
  if (key.includes('s20plus')) return ['128GB', '256GB', '512GB'];
  if (key.includes('s20fe')) return ['128GB', '256GB'];
  if (key.includes('s20')) return ['128GB'];

  // Galaxy S10
  if (key.includes('s10plus')) return ['128GB', '512GB', '1TB'];
  if (key.includes('s10e')) return ['128GB', '256GB'];
  if (key.includes('s10')) return ['128GB', '512GB'];

  // Galaxy S9
  if (key.includes('s9plus')) return ['64GB', '128GB', '256GB'];
  if (key.includes('s9')) return ['64GB', '128GB', '256GB'];

  // Galaxy S8
  if (key.includes('s8')) return ['64GB'];

  // Galaxy Note
  if (key.includes('note20ultra')) return ['128GB', '256GB', '512GB'];
  if (key.includes('note20')) return ['128GB', '256GB'];
  if (key.includes('note10lite')) return ['128GB'];
  if (key.includes('note10')) return ['256GB'];
  if (key.includes('note9')) return ['128GB', '512GB'];
  if (key.includes('note8')) return ['64GB', '128GB', '256GB'];

  // Galaxy Z (Fold/Flip)
  if (key.includes('fold6')) return ['256GB', '512GB', '1TB'];
  if (key.includes('flip6')) return ['256GB', '512GB'];
  if (key.includes('fold5')) return ['256GB', '512GB', '1TB'];
  if (key.includes('flip5')) return ['256GB', '512GB'];
  if (key.includes('fold4')) return ['256GB', '512GB', '1TB'];
  if (key.includes('flip4')) return ['128GB', '256GB', '512GB'];
  if (key.includes('fold3')) return ['256GB', '512GB'];
  if (key.includes('flip3')) return ['128GB', '256GB'];
  if (key.includes('fold2')) return ['256GB', '512GB'];
  if (key.includes('flip')) return ['256GB'];

  // Fallback
  return ['128GB', '256GB'];
};

const getIPadStorageValues = (modelNameRaw: string): string[] => {
  const name = (modelNameRaw || '').toLowerCase();
  const key = name.replace(/\s+|\-/g, '');

  // iPad Pro 2024 (M4)
  if (key.includes('ipadpro13m4') || key.includes('ipadpro137') || key.includes('ipadpro11-5') || key.includes('ipadpro115')) {
    return ['256GB', '512GB', '1TB', '2TB'];
  }
  // iPad Pro 12.9"
  if (key.includes('ipadpro1296')) return ['128GB', '256GB', '512GB', '1TB', '2TB']; // M2
  if (key.includes('ipadpro1295')) return ['128GB', '256GB', '512GB', '1TB', '2TB']; // M1
  if (key.includes('ipadpro1294')) return ['128GB', '256GB', '512GB', '1TB']; // A12Z
  if (key.includes('ipadpro1293')) return ['64GB', '256GB', '512GB', '1TB']; // A12X
  if (key.includes('ipadpro1292')) return ['64GB', '256GB', '512GB']; // A10X
  if (key.includes('ipadpro1291')) return ['32GB', '128GB', '256GB']; // A9X

  // iPad Pro 11"
  if (key.includes('ipadpro115')) return ['256GB', '512GB', '1TB', '2TB']; // M4 handled above too
  if (key.includes('ipadpro114')) return ['128GB', '256GB', '512GB', '1TB', '2TB']; // M2
  if (key.includes('ipadpro113')) return ['128GB', '256GB', '512GB', '1TB', '2TB']; // M1
  if (key.includes('ipadpro112')) return ['128GB', '256GB', '512GB', '1TB']; // A12Z
  if (key.includes('ipadpro111')) return ['64GB', '256GB', '512GB', '1TB']; // A12X

  // iPad Pro 10.5" & 9.7"
  if (key.includes('ipadpro105')) return ['64GB', '256GB', '512GB'];
  if (key.includes('ipadpro97')) return ['32GB', '128GB', '256GB'];

  // iPad Air (M2 2024)
  if (key.includes('ipadair613') || key.includes('ipadair611')) return ['128GB', '256GB', '512GB', '1TB'];
  // iPad Air 5/4/3/2
  if (key.includes('ipadair5')) return ['64GB', '256GB'];
  if (key.includes('ipadair4')) return ['64GB', '256GB'];
  if (key.includes('ipadair3')) return ['64GB', '256GB'];
  if (key.includes('ipadair2')) return ['16GB', '64GB', '128GB'];

  // iPad mini
  if (key.includes('ipadmini7')) return ['128GB', '256GB', '512GB'];
  if (key.includes('ipadmini6')) return ['64GB', '256GB'];
  if (key.includes('ipadmini5')) return ['64GB', '256GB'];
  if (key.includes('ipadmini4')) return ['16GB', '64GB', '128GB'];
  if (key.includes('ipadmini3')) return ['16GB', '64GB', '128GB'];

  // iPad (standaard)
  if (key.includes('ipad10')) return ['64GB', '256GB'];
  if (key.includes('ipad9')) return ['64GB', '256GB'];
  if (key.includes('ipad8')) return ['32GB', '128GB'];
  if (key.includes('ipad7')) return ['32GB', '128GB'];
  if (key.includes('ipad6')) return ['32GB', '128GB'];
  if (key.includes('ipad5')) return ['32GB', '128GB'];

  // Fallback
  return ['64GB', '256GB'];
};

const getMacBookStorageValues = (modelNameRaw: string): string[] => {
  const name = (modelNameRaw || '').toLowerCase();
  const key = name.replace(/\s+|\-/g, '');

  // Silicon generaties (Air / Pro)
  if (key.includes('macbookairsilicon') || (/macbook/.test(key) && /air/.test(key) && /silicon|m1|m2|m3|m4/.test(key))) {
    return ['256GB', '512GB', '1TB', '2TB'];
  }
  if (key.includes('macbookprosilicon') || (/macbook/.test(key) && /pro/.test(key) && /silicon|m1|m2|m3|m4/.test(key))) {
    return ['512GB', '1TB', '2TB', '4TB', '8TB'];
  }

  // Intel generaties
  if (key.includes('macbookprotouch')) {
    return ['256GB', '512GB', '1TB', '2TB', '4TB'];
  }
  if (key.includes('macbookairretina')) {
    return ['128GB', '256GB', '512GB', '1TB'];
  }
  if (key.includes('macbookproretina')) {
    return ['128GB', '256GB', '512GB', '1TB', '2TB'];
  }
  if (key.includes('macbookairlegacy') || (key.includes('macbookair') && !/silicon|retina/.test(key))) {
    return ['128GB', '256GB', '512GB'];
  }

  // Fallback voor overige MacBooks
  if (key.includes('macbook')) {
    return ['256GB', '512GB', '1TB'];
  }

  return ['256GB', '512GB'];
};

const getAppleWatchStorageValues = (modelNameRaw: string): string[] => {
  const name = (modelNameRaw || '').toLowerCase();
  const key = name.replace(/\s+|\-/g, '');

  if (key.includes('ultra2')) return ['64GB'];
  if (key.includes('ultra')) return ['32GB'];

  if (key.includes('series10')) return ['64GB'];
  if (key.includes('series9')) return ['64GB'];
  if (key.includes('series8')) return ['32GB'];
  if (key.includes('series7')) return ['32GB'];
  if (key.includes('series6')) return ['32GB'];
  if (key.includes('series5')) return ['32GB'];
  if (key.includes('series4')) return ['16GB'];

  if (key.includes('se2') || key.includes('se (2') || key.includes('se-2')) return ['32GB'];
  if (key.includes('se')) return ['32GB'];

  // Fallback voor onbekende watches
  if (key.includes('applewatch') || key.includes('watch')) return ['32GB'];

  return ['32GB'];
};

const getIMacStorageValues = (modelNameRaw: string): string[] => {
  const name = (modelNameRaw || '').toLowerCase();
  const key = name.replace(/\s+|\-/g, '');

  if (key.includes('imac24') || key.includes('imacm1') || key.includes('imacm3') || key.includes('imacm4')) {
    return ['256GB', '512GB', '1TB', '2TB'];
  }
  if (key.includes('imacpro')) {
    return ['512GB', '1TB', '2TB', '4TB'];
  }
  if (key.includes('imac27')) {
    return ['256GB', '512GB', '1TB', '2TB', '4TB', '8TB'];
  }

  // Fallback
  return ['256GB', '512GB', '1TB'];
};

// Format model name for storage lookup
const formatModelName = (raw: string): string => {
  if (!raw) return '';
  const tokens = raw
    .replace(/_/g, '-')
    .replace(/\s+/g, '-')
    .toLowerCase()
    .split('-')
    .filter(Boolean);

  const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const keepUpper = new Set(['se', 'fe', 'gb', 'tb', '5g']);
  const knownTitle = new Set([
    'samsung', 'galaxy', 'iphone', 'ipad', 'imac', 'macbook', 'apple', 'watch',
    'note', 'fold', 'flip', 'ultra', 'pro', 'max', 'mini', 'air', 'plus', 'series', 'z', 's'
  ]);

  const formatted: string[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (/^s\d+$/i.test(t)) {
      formatted.push('S' + t.replace(/^[a-z]/, ''));
      continue;
    }
    if (/^z\d+$/i.test(t)) {
      formatted.push('Z' + t.replace(/^[a-z]/, ''));
      continue;
    }
    if (keepUpper.has(t)) {
      formatted.push(t.toUpperCase());
      continue;
    }
    if (/^\d+$/.test(t)) {
      formatted.push(t);
      continue;
    }
    if (t === 'iphone') {
      formatted.push('iPhone');
      continue;
    }
    if (knownTitle.has(t)) {
      formatted.push(titleCase(t));
      continue;
    }
    formatted.push(titleCase(t));
  }

  // Merge patterns like ["S","23"] -> "S23"
  const merged: string[] = [];
  for (let i = 0; i < formatted.length; i++) {
    const cur = formatted[i];
    const next = formatted[i + 1];
    if (/^S$/i.test(cur) && /^\d+$/.test(next || '')) {
      merged.push(`S${next}`);
      i++; // skip next
      continue;
    }
    merged.push(cur);
  }

  return merged.join(' ').replace(/\bApple Watch\b/i, 'Apple Watch');
};

// Styled Components
const StorageIcon = () => (<HardDrive size={20} strokeWidth={1.75} />);

const StorageButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'selected',
}) <{ selected?: boolean }>`
  background: #ffffff;
  color: #1e293b;
  border: 1.5px solid #e2e8f0;
  padding: 1rem 1.25rem;
  border-radius: 16px;
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 96px;
  max-width: 280px;
  width: 100%;
  margin: 0 auto;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 16px;
    opacity: 0;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.04) 0%, rgba(99, 102, 241, 0.04) 100%);
    transition: opacity 0.2s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
    border-color: #94a3b8;
    &::before { opacity: 1; }
  }

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  ${props => props.selected && `
    border: 2px solid #1e293b;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.10);
    padding: calc(1rem - 0.5px) calc(1.25rem - 0.5px);
    &::before { opacity: 0; }
  `}

  .dark & {
    background: #0f1216;
    color: #e5e7eb;
    border-color: #262b36;
    &::before {
      background: linear-gradient(135deg, rgba(59, 130, 246, 0.06) 0%, rgba(99, 102, 241, 0.06) 100%);
    }
  }
  .dark &:hover {
    background: #151820;
    border-color: #3a4250;
    box-shadow: 0 8px 24px rgba(0,0,0,0.4);
  }
  ${props => props.selected && `
    .dark & {
      background: linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%);
      color: #f1f5f9;
      border: 2px solid #e2e8f0;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    }
  `}

  @media (max-width: 640px) {
    padding: 0.875rem 1rem;
    font-size: 0.95rem;
    border-radius: 14px;
    min-height: 84px;
    max-width: 100%;
  }
  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.9rem;
    min-height: 80px;
  }
  @media (max-width: 360px) {
    padding: 0.6rem;
    font-size: 0.85rem;
    min-height: 72px;
  }
`;

const QuestionContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible',
}) <{ visible: boolean }>`
  display: ${props => props.visible ? 'block' : 'none'};
`;

const QuestionTwoCol = styled.div`
  display: grid;
  grid-template-columns: 160px 1px 1fr;
  gap: 24px;
  align-items: start;
  min-width: 100%;
  width: 100%;
  
  > div:last-child {
    min-width: 600px;
    width: 100%;
  }
  
  @media (max-width: 1024px) {
    grid-template-columns: 150px 1px 1fr;
    > div:last-child {
      min-width: 500px;
    }
  }
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    > div:last-child {
      min-width: 100%;
    }
  }
`;

const HelperRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 24px;
  align-items: start;
  margin-top: 0;
  @media (max-width: 1024px) {
    grid-template-columns: 150px 1fr;
  }
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const IllustrationPanel = styled.div`
  display: none;
  @media (min-width: 861px) {
    display: block;
  }
  margin: 0;
`;

const ColumnDivider = styled.div`
  display: none;
  @media (min-width: 861px) {
    display: block;
    align-self: stretch;
    justify-self: center;
    width: 1px;
    background: linear-gradient(180deg, rgba(15,23,42,0.10) 0%, rgba(15,23,42,0.04) 100%);
    border-radius: 1px;
    transform: translateX(-16px);
    @media (min-width: 1280px) {
      transform: translateX(-22px);
    }
  }
`;

const IllustrationCard = styled.div`
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  height: 380px;
  position: relative;
  overflow: hidden;
  display: block;
  margin-left: -1.25rem;
  box-shadow: none;
`;

const PhoneIllustrationFrame = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 0;

  img, .device-image {
    height: 100%;
    width: auto;
    max-width: none;
    object-fit: contain;
    display: block;
    filter: drop-shadow(0 12px 20px rgba(0,0,0,0.12));
    transform: translateX(-40px);
    will-change: transform, opacity;
    animation: ${phoneSlideIn} 1200ms cubic-bezier(0.16, 1, 0.3, 1) 100ms both;
  }

  @media (min-width: 1280px) {
    img, .device-image {
      height: 100%;
      transform: translateX(-64px);
      animation: ${phoneSlideInWide} 1400ms cubic-bezier(0.16, 1, 0.3, 1) 120ms both;
    }
  }
`;

const OptionsGrid = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'columns',
}) <{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 3}, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 0.5rem;
  justify-content: stretch;
  width: 100%;
  
  ${props => props.columns === 2 && `
    max-width: 600px;
    margin-left: 0;
    margin-right: auto;
  `}
  ${props => props.columns === 3 && `
    max-width: 900px;
    margin-left: 0;
    margin-right: auto;
  `}
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(${props => Math.min(2, (props.columns || 3))}, minmax(180px, 1fr));
    ${props => props.columns === 2 && `
      max-width: 500px;
    `}
    ${props => props.columns === 3 && `
      max-width: 100%;
    `}
  }
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.625rem;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    margin-bottom: 0.15rem;
  }
`;

const HelperText = styled.div`
  margin-top: 2rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #f8fafc 100%);
  border-radius: 14px;
  border: 1px solid #e0ecf4;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  transition: all 0.2s ease;

  .icon-box {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    background: #1e293b;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;

    svg {
      width: 16px;
      height: 16px;
      stroke-width: 2.5;
    }
  }

  .content-box {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  h4 {
    font-size: 0.82rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    line-height: 1.3;
  }

  p {
    font-size: 0.8rem;
    color: #64748b;
    margin: 0;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1rem;
    gap: 10px;
    margin-top: 1.5rem;

    .icon-box { width: 30px; height: 30px; border-radius: 8px; svg { width: 14px; height: 14px; } }
    h4 { font-size: 0.8rem; }
    p { font-size: 0.75rem; }
  }

  .dark & {
    background: linear-gradient(135deg, rgba(30, 41, 59, 0.5) 0%, rgba(15, 23, 42, 0.4) 100%);
    border-color: #262b36;

    .icon-box {
      background: #e2e8f0;
      color: #0f172a;
    }

    h4 { color: #f1f5f9; }
    p { color: #94a3b8; }
  }
`;

// Enhanced device image component with fallback system
const DeviceImageWithFallback: React.FC<{
  modelName: string;
  alt: string;
  className?: string;
  imageSrc?: string; // Database image URL (takes precedence over generated paths)
}> = ({ modelName, alt, className, imageSrc }) => {
  const [currentImagePath, setCurrentImagePath] = useState<string>('');
  const [imageError, setImageError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Get multiple image variants in quality priority order (highest quality first)
  const getImageVariants = (modelName: string): string[] => {
    const name = modelName.toLowerCase().replace(/\s+/g, '-');

    const baseVariants: string[] = [];

    if (name.includes('iphone')) {
      const iphoneModel = name.replace('iphone-', '').replace(/[^a-z0-9-]/g, '-');
      const pascalModel = iphoneModel.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('-');
      baseVariants.push(
        `/devices/iphone-${iphoneModel}.png`,
        `/devices/Apple-Iphone-${pascalModel}-PNG.png`,
        `/devices/iphone-${iphoneModel}-hd.png`,
        `/devices/iphone-${iphoneModel}-high.png`,
        `/devices/iphone-${iphoneModel}-medium-png.png`,
      );
      const genMatch = iphoneModel.match(/^(\d+)/);
      if (genMatch) {
        const prevGen = parseInt(genMatch[1]) - 1;
        if (prevGen >= 7) {
          baseVariants.push(
            `/devices/iphone-${prevGen}-pro-max.png`,
            `/devices/iphone-${prevGen}-pro.png`,
            `/devices/iphone-${prevGen}.png`,
          );
        }
      }
      baseVariants.push(
        `/devices/iphone-medium-png.png`,
        `/devices/iphone.png`
      );
    } else if (name.includes('ipad')) {
      const ipadModel = name.replace('ipad-', '').replace(/[^a-z0-9-]/g, '-');

      if (ipadModel.includes('pro-13-7') || ipadModel.includes('pro-13')) {
        baseVariants.push(
          `/iPad/ipad-pro-13inch-7de-gen-medium-png.png`,
          `/devices/ipad-pro-13-7.png`
        );
      } else if (ipadModel.includes('pro-12-9-6') || (ipadModel.includes('pro') && ipadModel.includes('12') && ipadModel.includes('6'))) {
        baseVariants.push(
          `/iPad/ipad-pro-12,9inch-6de-gen-medium-png.png`,
          `/devices/ipad-pro-12-9-6.png`
        );
      } else if (ipadModel.includes('pro-12-9-5') || (ipadModel.includes('pro') && ipadModel.includes('12') && ipadModel.includes('5'))) {
        baseVariants.push(
          `/iPad/ipad-pro-12,9inch-5de-gen-medium-png.png`,
          `/devices/ipad-pro-12-9-5.png`
        );
      } else if (ipadModel.includes('pro-12-9-4') || (ipadModel.includes('pro') && ipadModel.includes('12') && ipadModel.includes('4'))) {
        baseVariants.push(
          `/iPad/ipad-pro-12,9inch-4de-gen-medium-png.png`,
          `/devices/ipad-pro-12-9-4.png`
        );
      } else {
        baseVariants.push(
          `/devices/ipad-${ipadModel}.png`,
          `/devices/ipad-${ipadModel}-hd.png`,
          `/devices/ipad.png`
        );
      }
    } else if (name.includes('macbook')) {
      const macbookModel = name.replace('macbook-', '').replace(/[^a-z0-9-]/g, '-');
      baseVariants.push(
        `/devices/macbook-${macbookModel}.png`,
        `/devices/macbook-${macbookModel}-hd.png`,
        `/devices/macbook.png`
      );
    } else if (name.includes('samsung') || name.includes('galaxy')) {
      const samsungModel = name.replace(/^(samsung-|galaxy-)/, '').replace(/[^a-z0-9-]/g, '-');
      baseVariants.push(
        `/devices/galaxy-${samsungModel}.png`,
        `/devices/galaxy-${samsungModel}-hd.png`,
        `/devices/galaxy.png`
      );
    } else {
      baseVariants.push(`/devices/${name}.png`, `/devices/device.png`);
    }

    return baseVariants;
  };

  const loadIdRef = React.useRef<number>(0);

  React.useEffect(() => {
    if (!modelName) {
      setIsLoading(false);
      return;
    }

    const thisLoadId = ++loadIdRef.current;

    // Build variants array - prioritize imageSrc from database if provided
    const fallbackVariants = getImageVariants(modelName);
    const variants = imageSrc && imageSrc !== '/devices/default.png'
      ? [imageSrc, ...fallbackVariants]
      : fallbackVariants;

    let variantIndex = 0;
    let cancelled = false;

    const tryNextVariant = () => {
      if (cancelled || loadIdRef.current !== thisLoadId) return;
      if (variantIndex >= variants.length) {
        if (!currentImagePath) setImageError(true);
        setIsLoading(false);
        return;
      }

      const img = new Image();
      const currentVariant = variants[variantIndex];

      img.onload = () => {
        if (cancelled || loadIdRef.current !== thisLoadId) return;
        setCurrentImagePath(currentVariant);
        setImageError(false);
        setIsLoading(false);
      };

      img.onerror = () => {
        if (cancelled || loadIdRef.current !== thisLoadId) return;
        variantIndex++;
        tryNextVariant();
      };

      img.src = encodeURI(currentVariant);
    };

    // Only show loading spinner on first load
    if (!currentImagePath) {
      setIsLoading(true);
    }
    setImageError(false);
    tryNextVariant();

    return () => { cancelled = true; };
  }, [modelName, imageSrc]);

  if (!modelName) return null;

  if (imageError || !currentImagePath) {
    return (
      <div className={className} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '0.875rem' }}>
        {alt}
      </div>
    );
  }

  return (
    <img
      src={encodeURI(currentImagePath)}
      alt={alt}
      className={className}
      style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
      loading="lazy"
    />
  );
};

// Component Props
interface StorageStepProps {
  modelName: string;
  selectedStorage: string | null;
  onSelect: (storage: string) => void;
  t: (key: string) => string;
  visible: boolean;
  availableStorage?: string[];
  imageSrc?: string; // Database image URL for device illustration
}

// Main Component
const StorageStep: React.FC<StorageStepProps> = ({
  modelName,
  selectedStorage,
  onSelect,
  t,
  visible,
  availableStorage,
  imageSrc,
}) => {
  const [storageImageError, setStorageImageError] = useState<boolean>(false);

  // Build storage options dynamically based on model
  // Use DB-provided storage options if available (Single Source of Truth)
  // Otherwise fallback to hardcoded logic
  let dynamicStorageValues: string[] = [];

  if (availableStorage && availableStorage.length > 0) {
    dynamicStorageValues = availableStorage;
  } else {
    // Fallback logic
    dynamicStorageValues = ['256GB', '512GB', '1TB'];
    const formattedModelName = formatModelName(modelName);
    if (/iphone/i.test(modelName)) {
      dynamicStorageValues = getIphoneStorageValues(formattedModelName);
    } else if (/galaxy|samsung|note|fold|flip/i.test(modelName)) {
      dynamicStorageValues = getSamsungStorageValues(formattedModelName);
    } else if (/ipad|imac|macbook/i.test(modelName) && /ipad/i.test(modelName)) {
      dynamicStorageValues = getIPadStorageValues(formattedModelName);
    } else if (/macbook/i.test(modelName)) {
      dynamicStorageValues = getMacBookStorageValues(formattedModelName);
    } else if (/apple\s*watch|watch/i.test(modelName)) {
      dynamicStorageValues = getAppleWatchStorageValues(formattedModelName);
    } else if (/imac/i.test(modelName)) {
      dynamicStorageValues = getIMacStorageValues(formattedModelName);
    }
  }

  const storageOptions = dynamicStorageValues.map(v => ({ value: v, icon: (<StorageIcon />), description: '' }));

  // Debug log for verification
  if (visible) {
    console.log('[STORAGE_STEP_OPTIONS]', storageOptions);
  }

  return (
    <QuestionContainer visible={visible}>
      <AnimatePresence mode="wait">
        {visible && (
          <motion.div
            key="storage-step"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
          >
            <QuestionTwoCol>
              <IllustrationPanel>
                <IllustrationCard>
                  <PhoneIllustrationFrame>
                    {!storageImageError ? (
                      <img
                        src="/images/storage-illustration.avif"
                        alt={t('storageIllustrationAlt')}
                        draggable={false}
                        loading="eager"
                        onError={() => {
                          setStorageImageError(true);
                        }}
                        style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                      />
                    ) : (
                      <DeviceImageWithFallback modelName={modelName} alt={modelName} imageSrc={imageSrc} />
                    )}
                  </PhoneIllustrationFrame>
                </IllustrationCard>
              </IllustrationPanel>
              <ColumnDivider />
              <div>
                <OptionsGrid columns={3}>
                  {storageOptions.map((option) => (
                    <StorageButton
                      key={option.value}
                      selected={selectedStorage === option.value}
                      onClick={() => onSelect(option.value)}
                    >
                      {option.value}
                    </StorageButton>
                  ))}
                </OptionsGrid>
              </div>
            </QuestionTwoCol>

            <HelperRow>
              <div />
              <HelperText>
                <div className="icon-box">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                </div>
                <div className="content-box">
                  <h4>{t('howToCheckStorage')}</h4>
                  <p>{t('storageInstructionIphone')}</p>
                </div>
              </HelperText>
            </HelperRow>
          </motion.div>
        )}
      </AnimatePresence>
    </QuestionContainer>
  );
};

export default StorageStep;

